import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete, ContentCopy, Settings, UploadFile } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';
import { useBusiness } from '../../contexts/BusinessContext';

const SCHEMA_SOURCES = [
  { value: 'admin_defined', label: 'Admin-defined' },
  { value: 'dhis2_metadata', label: 'DHIS2 metadata' },
  { value: 'dhis2_program_data_json', label: 'ProgramData JSON' },
] as const;

function isWebAdapterConfig(c: Dhis2ConfigOut): boolean {
  const schemaSource = String(c.schema_source ?? '').toLowerCase();
  const configType = String(c.config_type ?? '').toLowerCase();
  return schemaSource === 'admin_defined' || configType === 'dynamic_form';
}

interface ConfigListProps {
  onOpenConnection: (configId: string) => void;
  onMutated?: () => void;
}

export default function ConfigList({ onOpenConnection, onMutated }: ConfigListProps) {
  const { tenants, activeTenantId } = useBusiness();
  const entitiesForTenant = useMemo(() => {
    const t = tenants.find((x) => x.tenant_id === activeTenantId);
    return t?.entities ?? [];
  }, [tenants, activeTenantId]);

  const [configs, setConfigs] = useState<Dhis2ConfigOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [schemaSource, setSchemaSource] = useState<string>('admin_defined');
  const [saving, setSaving] = useState(false);
  /** Optional entity link for new config / OPD-from-template (requires X-Tenant-ID from client). */
  const [entityIdForNew, setEntityIdForNew] = useState<string>('');

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<string | null>(null);
  const [cloneNameInput, setCloneNameInput] = useState('');
  const [cloneEntityId, setCloneEntityId] = useState<string>('');

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.listConfigs();
      setConfigs(Array.isArray(res.data) ? res.data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load configs');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleAdd = () => {
    setEditingId(null);
    setName('');
    setSchemaSource('admin_defined');
    setDialogOpen(true);
  };

  const handleAddWebAdapterFromOpdTemplate = async () => {
    const newName = window.prompt('Name for new web adapter config:', 'New Web Adapter Config') ?? '';
    if (!newName.trim()) return;
    setError(null);
    setSaving(true);
    try {
      const template = await dhis2ConfigAPI.getWebOpdDefaultTemplate();
      await dhis2ConfigAPI.createConfig({
        name: newName.trim(),
        schema_source: 'admin_defined',
        form_schema_json: template.data.form_schema_json,
        field_mappings_json: template.data.field_mappings_json,
        extraction_prompt_text: template.data.extraction_prompt_text,
        ...(entityIdForNew ? { entity_id: entityIdForNew } : {}),
      });
      await loadConfigs();
      onMutated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create web adapter config from OPD template');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c: Dhis2ConfigOut) => {
    setEditingId(c.id);
    setName(c.name);
    setSchemaSource(c.schema_source);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await dhis2ConfigAPI.updateConfig(editingId, { name: name.trim(), schema_source: schemaSource });
      } else {
        await dhis2ConfigAPI.createConfig({
          name: name.trim(),
          schema_source: schemaSource,
          ...(entityIdForNew ? { entity_id: entityIdForNew } : {}),
        });
      }
      setDialogOpen(false);
      await loadConfigs();
      onMutated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!window.confirm('Delete this config and its connection? This cannot be undone.')) return;
    setError(null);
    try {
      await dhis2ConfigAPI.deleteConfig(configId);
      await loadConfigs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const openCloneDialog = (configId: string) => {
    const suggested = configs.find((c) => c.id === configId)?.name ?? 'Config';
    setCloneSourceId(configId);
    setCloneNameInput(`Copy of ${suggested}`);
    setCloneEntityId('');
    setCloneOpen(true);
  };

  const handleCloneConfirm = async () => {
    if (!cloneSourceId || !cloneNameInput.trim()) return;
    setError(null);
    setSaving(true);
    try {
      await dhis2ConfigAPI.cloneConfig(cloneSourceId, {
        name: cloneNameInput.trim(),
        ...(cloneEntityId ? { entity_id: cloneEntityId } : {}),
      });
      setCloneOpen(false);
      setCloneSourceId(null);
      await loadConfigs();
      onMutated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Clone failed');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyWebOpdTemplate = async (configId: string, configName: string) => {
    if (!window.confirm(`Apply current OPD template to "${configName}"?\nThis will overwrite Form Schema and Extraction Prompt.`)) return;
    setError(null);
    try {
      await dhis2ConfigAPI.applyWebOpdTemplate(configId);
      await loadConfigs();
      onMutated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Apply OPD template failed');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Configurations</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {entitiesForTenant.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Link new config to entity</InputLabel>
              <Select
                value={entityIdForNew}
                label="Link new config to entity"
                onChange={(e) => setEntityIdForNew(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {entitiesForTenant.map((e) => (
                  <MenuItem key={e.entity_id} value={e.entity_id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={handleAddWebAdapterFromOpdTemplate}
            disabled={saving}
          >
            New web adapter (OPD template)
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            New config
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table size="small" sx={{ minWidth: 360 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Schema source</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>Loading…</TableCell>
              </TableRow>
            ) : configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>No configs. Create one to get started.</TableCell>
              </TableRow>
            ) : (
              configs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    {SCHEMA_SOURCES.find((s) => s.value === c.schema_source)?.label ?? c.schema_source}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Connection & Schema API" onClick={() => onOpenConnection(c.id)}>
                      <Settings />
                    </IconButton>
                    <IconButton size="small" title="Edit" onClick={() => handleEdit(c)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" title="Clone" onClick={() => openCloneDialog(c.id)}>
                      <ContentCopy />
                    </IconButton>
                    {isWebAdapterConfig(c) && (
                      <IconButton
                        size="small"
                        title="Apply OPD template (schema + prompt)"
                        onClick={() => handleApplyWebOpdTemplate(c.id, c.name)}
                      >
                        <UploadFile />
                      </IconButton>
                    )}
                    <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(c.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit config' : 'New config'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Schema source</InputLabel>
            <Select
              value={schemaSource}
              label="Schema source"
              onChange={(e) => setSchemaSource(e.target.value)}
            >
              {SCHEMA_SOURCES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Admin-defined: non-DHIS2 (internal/stream-only). DHIS2 metadata/ProgramData: set Connection & Schema API for this config.
            </Typography>
          </FormControl>
          {!editingId && entitiesForTenant.length > 0 && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Entity</InputLabel>
              <Select
                value={entityIdForNew}
                label="Entity"
                onChange={(e) => setEntityIdForNew(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {entitiesForTenant.map((e) => (
                  <MenuItem key={e.entity_id} value={e.entity_id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Optional: attach this config to an entity (requires tenant context / X-Tenant-ID).
              </Typography>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone configuration</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name for cloned config"
            fullWidth
            value={cloneNameInput}
            onChange={(e) => setCloneNameInput(e.target.value)}
          />
          {entitiesForTenant.length > 0 && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Also link to entity</InputLabel>
              <Select
                value={cloneEntityId}
                label="Also link to entity"
                onChange={(e) => setCloneEntityId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None (keep copied entity links only)</em>
                </MenuItem>
                {entitiesForTenant.map((e) => (
                  <MenuItem key={e.entity_id} value={e.entity_id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Cloning copies all entity links from the source; optionally add another link here.
              </Typography>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCloneConfirm} disabled={!cloneNameInput.trim() || saving}>
            {saving ? 'Cloning…' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
